import { Link } from 'react-router'
import { Card, CardContent } from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { formatPrice } from '#app/utils/currency.ts'
import { Badge } from './ui/badge'

interface RentalCardProps {
	rental: {
		id: string
		title: string
		slug: string
		description: string | null
		hourlyRateCents: number | null
		dailyRateCents: number | null
		weeklyRateCents: number | null
		monthlyRateCents: number | null
		featuredMedia?: {
			url: string | null
			altText: string | null
		} | null
		categories?: Array<{
			id: string
			name: string
			slug: string
		}>
		subcategories?: Array<{
			id: string
			name: string
			slug: string
		}>
		cities?: Array<{
			id: string
			name: string
			slug: string
		}>
		ownerBusiness?: {
			id: string
			name: string
			slug: string
		} | null
		ownerUser?: {
			id: string
			name: string | null
		} | null
	}
}

export function RentalCard({ rental }: RentalCardProps) {
	// Determine the best price to display (prefer hourly, then daily, then weekly, then monthly)
	const primaryRate =
		rental.hourlyRateCents ??
		rental.dailyRateCents ??
		rental.weeklyRateCents ??
		rental.monthlyRateCents

	const primaryRateLabel =
		rental.hourlyRateCents != null
			? '/hour'
			: rental.dailyRateCents != null
				? '/day'
				: rental.weeklyRateCents != null
					? '/week'
					: rental.monthlyRateCents != null
						? '/month'
						: undefined

	return (
		<Link to={`/rentals/${rental.slug}`} className="group">
			<Card className="dark:group-hover:ring-offset-background h-full transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-xl dark:group-hover:ring-4 dark:group-hover:ring-white/10">
				<CardContent className="flex flex-col">
					{rental.featuredMedia?.url && (
						<div className="relative -mx-6 -mt-6 mb-5 h-64 overflow-hidden rounded-t-lg">
							<img
								src={rental.featuredMedia.url}
								alt={rental.featuredMedia.altText || rental.title}
								loading="lazy"
								decoding="async"
								className="h-full w-full object-cover"
							/>
						</div>
					)}

					<div className="space-y-3">
						{rental.categories && rental.categories.length > 0 && (
							<div className="flex flex-wrap items-center justify-between gap-1">
								{rental.categories.length === 1 ? (
									<div className="text-xs font-bold text-red-600 uppercase">
										{rental.categories[0]?.name}
										{rental.subcategories &&
											rental.subcategories.length > 0 && (
												<span className="ml-1">
													+{rental.subcategories.length}
												</span>
											)}
									</div>
								) : (
									<div className="text-xs font-bold text-red-600 uppercase">
										{rental.categories[0]?.name} +{rental.categories.length - 1}
										{rental.subcategories &&
											rental.subcategories.length > 0 && (
												<span className="ml-1">
													(+{rental.subcategories.length} sub)
												</span>
											)}
									</div>
								)}

								{rental.ownerBusiness ? (
									<>
										<Badge variant="secondary">Local Business</Badge>
									</>
								) : rental.ownerUser ? (
									<Badge variant="secondary">Private Owner</Badge>
								) : null}
							</div>
						)}
						<div className="flex flex-col gap-1">
							<h3 className="line-clamp-2 text-lg leading-tight font-bold">
								{rental.title}
							</h3>
							{rental.cities && rental.cities.length > 0 && (
								<div className="flex flex-wrap gap-1">
									{rental.cities.slice(0, 1).map((city) => (
										<div
											key={city.id}
											className="text-muted-foreground flex items-center gap-1 text-sm"
										>
											<Icon name="map-pin" className="size-3" />
											{city.name}
										</div>
									))}
									{rental.cities.length > 1 && (
										<div className="text-muted-foreground text-sm">
											+{rental.cities.length - 1}
										</div>
									)}
								</div>
							)}
						</div>

						<div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
							<div className="flex items-center gap-2">
								<div className="bg-muted flex size-8 items-center justify-center rounded-full">
									<Icon
										name={rental.ownerBusiness ? 'building-2' : 'avatar'}
										className="size-4 opacity-60"
									/>
								</div>
								<div className="flex flex-col">
									{rental.ownerBusiness ? (
										<>
											<span className="block text-sm leading-none font-bold">
												{rental.ownerBusiness.name}
											</span>
										</>
									) : rental.ownerUser ? (
										<div className="flex flex-col">
											<span className="block text-sm leading-none font-bold">
												{rental.ownerUser.name}
											</span>
										</div>
									) : null}
								</div>
							</div>

							<div className="flex items-baseline justify-between">
								<span className="text-xl font-bold">
									{primaryRate ? formatPrice(primaryRate) : 'Contact'}
								</span>
								{primaryRate && (
									<span className="text-muted-foreground">
										{primaryRateLabel}
									</span>
								)}
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</Link>
	)
}
